import { fetcher } from '../../../../lib/api-fetcher';
import { createFileRoute, redirect, useNavigate, useSearch, Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { getSession } from '../../../../lib/session';
import { defineListPage, useListPage } from '@repo/list';
import type { PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { usePageTitle } from '../../../../lib/hooks';
import type { StaffMember } from '../../../../features/staff/network/staff.api';
import { StaffActions } from '../../../../features/staff/components/StaffActions';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/Button';
import { Plus } from 'lucide-react';

const staffConfig = defineListPage<StaffMember>({
  variant: 'data-table',
  resource: 'staff',
  searchable: true,
  columns: [
    { id: 'name', header: 'Name', accessor: 'name', sortable: true },
    { id: 'email', header: 'Email Address', accessor: 'email', renderType: 'email' },
    { id: 'phoneNumber', header: 'Mobile Number', accessor: 'phoneNumber', renderType: 'phone' },
    { id: 'roleName', header: 'Role', accessor: 'roleName' },
    { id: 'orgUnitName', header: 'Branch', accessor: 'orgUnitName' },
    { id: 'status', header: 'Status', accessor: 'status' },
    { id: 'actions', header: '', accessor: () => null, width: 60 },
  ],
  filters: [
    {
      id: 'status',
      label: 'Status',
      type: 'select',
      options: [
        { label: 'Active', value: 'active' },
        { label: 'Pending Activation', value: 'pending_activation' },
        { label: 'Reset Needed', value: 'pending_password_reset' },
        { label: 'Suspended', value: 'suspended' },
      ],
    },
  ],
  defaultPageSize: 20,
});

const fetchStaff: PaginatedQueryFn<StaffMember> = async (params) => {
  const page = parseInt(params.page || '1', 10) || 1;
  const limit = parseInt(params.limit || '20', 10) || 20;
  const all = await fetcher.get<StaffMember[]>('/staff/v1/admin/staff');
  const filtered = params.status ? all.filter((s) => s.status === params.status) : all;
  const search = params.search?.toLowerCase();
  const searched = search
    ? filtered.filter((s) => s.name.toLowerCase().includes(search) || s.email.toLowerCase().includes(search))
    : filtered;
  const start = (page - 1) * limit;
  return {
    data: searched.slice(start, start + limit),
    meta: { total: searched.length, page, limit },
  };
};

export const Route = createFileRoute('/_auth/admin/staff/')({
  beforeLoad: ({ location }) => {
    const session = getSession();
    const isAllowed =
      session?.user.type === 'group_admin' ||
      session?.user.type === 'company_admin' ||
      session?.permissions.includes('admin.staff.read');
    if (!isAllowed) {
      throw redirect({ to: '/', search: { callbackUrl: location.href } });
    }
  },
  component: StaffManagementPage,
});

function StaffManagementPage() {
  usePageTitle('Staff Management');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });

  const urlAdapter = useMemo(() => createUrlAdapter(search, navigate as never), [search, navigate]);

  const list = useListPage(staffConfig, fetchStaff, urlAdapter);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Staff Management"
        subtitle="Manage employees, roles, and branch assignments."
        action={
          <Button asChild variant="primary">
            <Link to="/admin/staff/new">
              <Plus className="h-4 w-4 mr-1.5" />
              Add Staff
            </Link>
          </Button>
        }
      />

      <DataTable
        config={staffConfig}
        list={list}
        customRenderers={{
          name: (row) => (
            <Link to="/admin/staff/$id" params={{ id: row.id }} className="font-medium text-accent-500 hover:underline">
              {row.name}
            </Link>
          ),
          status: (row) => {
            const status = row.status;
            let badgeStatus: 'success' | 'warning' | 'danger' | 'info' = 'info';
            let label = 'Unknown';
            if (status === 'active') {
              badgeStatus = 'success';
              label = 'Active';
            } else if (status === 'pending_password_reset') {
              badgeStatus = 'warning';
              label = 'Reset Needed';
            } else if (status === 'pending_activation') {
              badgeStatus = 'info';
              label = 'Pending Activation';
            } else if (status === 'suspended') {
              badgeStatus = 'danger';
              label = 'Suspended';
            }
            return <StatusBadge status={badgeStatus} label={label} />;
          },
          actions: (row) => <StaffActions member={row} />,
        }}
      />
    </div>
  );
}
